import React, { useState, useCallback } from 'react';
import { Upload, Loader2, Music, Brain, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WaveformVisualizer } from './WaveformVisualizer';
import { PresetSelector } from './PresetSelector';
import { AudioControls } from './AudioControls';
import { AudioDownloader } from './AudioDownloader';
import * as Tone from 'tone';
import { ProcessingOptions } from '../types';
import { audioUploadSchema } from '../lib/validation';

interface AudioUploaderProps {
  onProcessingStart: () => void;
  onProcessingComplete: (url: string) => void;
  onProgress: (progress: number) => void;
}

export function AudioUploader({ onProcessingStart, onProcessingComplete, onProgress }: AudioUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewPlayer, setPreviewPlayer] = useState<Tone.Player | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('modern-punch');
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);

  const processAudioFile = async (file: File) => {
    try {
      setIsUploading(true);
      setError(null);
      onProcessingStart();
      onProgress(0);

      // Stop playback if active
      if (previewPlayer && isPlaying) {
        previewPlayer.stop();
        setIsPlaying(false);
      }

      // Validate file and preset
      const validationResult = audioUploadSchema.safeParse({
        file,
        preset: selectedPreset
      });

      if (!validationResult.success) {
        throw new Error(validationResult.error.errors[0].message);
      }

      const formData = new FormData();
      formData.append('audio', file);
      formData.append('preset', selectedPreset);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/audio-processing`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process audio');
      }

      if (!data.success || !data.data?.url) {
        throw new Error('Invalid response format from server');
      }

      setProcessedUrl(data.data.url);
      onProcessingComplete(data.data.url);

      if (previewPlayer) {
        previewPlayer.disconnect();
      }

      const player = new Tone.Player(data.data.url).toDestination();
      player.volume.value = isMuted ? -Infinity : Tone.gainToDb(volume);
      setPreviewPlayer(player);
      onProgress(100);
    } catch (err) {
      console.error('Processing error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      onProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMainUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCurrentFile(file);
    await processAudioFile(file);
  };

  const handlePresetChange = async (preset: string) => {
    setSelectedPreset(preset);
    if (currentFile) {
      await processAudioFile(currentFile);
    }
  };

  const handleRegenerate = async () => {
    if (currentFile) {
      await processAudioFile(currentFile);
    }
  };

  const togglePlayback = async () => {
    if (!previewPlayer) return;

    await Tone.start();
    
    if (isPlaying) {
      previewPlayer.stop();
    } else {
      previewPlayer.start();
    }
    setIsPlaying(!isPlaying);
  };

  const resetPlayback = () => {
    if (previewPlayer) {
      previewPlayer.stop();
      previewPlayer.seek(0);
      setIsPlaying(false);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (previewPlayer) {
      previewPlayer.volume.value = Tone.gainToDb(newVolume);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (previewPlayer) {
      previewPlayer.volume.value = !isMuted ? -Infinity : Tone.gainToDb(volume);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <PresetSelector
          selectedPreset={selectedPreset}
          onPresetChange={handlePresetChange}
        />
        {processedUrl && !isUploading && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRegenerate}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 
                     hover:bg-blue-500/30 transition-colors duration-300"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Regenerate</span>
          </motion.button>
        )}
      </div>

      <label 
        htmlFor="audio-upload"
        className={`
          relative flex flex-col items-center justify-center w-full h-40
          border-2 border-dashed rounded-xl cursor-pointer
          transition-all duration-300
          ${isUploading 
            ? 'border-blue-500/50 bg-blue-900/20' 
            : 'border-gray-700 hover:border-blue-500/50 hover:bg-gray-800/50'
          }
        `}
      >
        <input
          id="audio-upload"
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={handleMainUpload}
          disabled={isUploading}
        />
        
        {isUploading ? (
          <div className="flex items-center space-x-3 text-blue-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-medium">Processing your audio...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-3">
            <Upload className="w-8 h-8 text-gray-500" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-300">
                Upload your audio here
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports WAV, MP3, AIFF (max 50MB)
              </p>
            </div>
          </div>
        )}
      </label>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-3 p-4 bg-red-900/20 text-red-400 rounded-lg border border-red-500/20"
          >
            <Brain className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm flex-grow">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {processedUrl && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-300">Mastered Audio</h3>
              <AudioControls
                isPlaying={isPlaying}
                onPlayPause={togglePlayback}
                onReset={resetPlayback}
                volume={volume}
                onVolumeChange={handleVolumeChange}
                isMuted={isMuted}
                onMuteToggle={handleMuteToggle}
              />
            </div>
            
            <WaveformVisualizer 
              audioUrl={processedUrl} 
              isProcessing={isUploading}
            />

            <div className="mt-6 pt-6 border-t border-gray-700/50">
              <AudioDownloader 
                audioUrl={processedUrl}
                originalFileName={currentFile?.name ?? 'audio'}
              />
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}